import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import { getCartItems, clearCart } from "../../utils/cartUtils";
import { toLocalizedNumber, toLocalizedPrice } from "../../utils/localeUtils";
import { getImageUrl } from "../../utils/imageUtils";
import { FaCheck } from "react-icons/fa";
import "./Checkout.css";

const Checkout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    district: "Morang",
    province: "Province 1",
    deliveryMethod: "home",
    paymentMethod: "cod",
    specialInstructions: "",
  });

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "consumer") {
          navigate("/");
          return;
        }
        setFormData((prev) => ({
          ...prev,
          fullName: userObj.name || "",
          email: userObj.email || "",
          phone: userObj.phone || "",
          address: userObj.address?.street || userObj.address || "",
          city: userObj.address?.municipality || userObj.address?.city || "",
          district: userObj.address?.district || "Morang",
          province: userObj.address?.province || "Province 1",
        }));
      } catch (err) {
        console.error("Error parsing user data:", err);
      }
    } else {
      navigate("/login");
      return;
    }

    const productId = searchParams.get("product");
    const quantity = parseInt(searchParams.get("quantity") || "1");

    if (productId) {
      fetchProductForCheckout(productId, quantity);
    } else {
      loadCartItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);

  const fetchProductForCheckout = async (productId, quantity) => {
    try {
      const response = await api.get(`/products/${productId}`);
      const product = response.data;
      setCartItems([
        {
          _id: product._id,
          product: {
            _id: product._id,
            name: product.name,
            price: product.price,
            unit: product.unit,
            images: product.images || [],
          },
          quantity: quantity,
          subtotal: product.price * quantity,
        },
      ]);
    } catch (err) {
      console.error("Error fetching product:", err);
      setError(t("checkout.loadFailed"));
    }
  };

  const loadCartItems = () => {
    const items = getCartItems();
    if (items.length === 0) {
      setError(t("checkout.emptyCart"));
      setTimeout(() => navigate("/cart"), 2000);
      return;
    }
    setCartItems(items);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const selectOption = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (cartItems.length === 0) {
      setError(t("checkout.emptyCart"));
      setLoading(false);
      return;
    }

    try {
      const items = cartItems.map((item) => ({
        product: item.product._id || item.product,
        quantity: item.quantity,
      }));

      const shippingAddress = {
        street: formData.address,
        city: formData.city,
        district: formData.district,
        phone: formData.phone,
        fullName: formData.fullName,
        email: formData.email,
        province: formData.province,
        deliveryMethod: formData.deliveryMethod,
        specialInstructions: formData.specialInstructions,
      };

      const selectedMethod = formData.paymentMethod;
      const paymentMethod = selectedMethod === "cod" ? "COD" : "online";
      const paymentGateway = selectedMethod === "cod" ? null : selectedMethod;

      const response = await api.post("/orders", {
        items,
        shippingAddress,
        paymentMethod,
        paymentGateway,
      });

      const order = response.data;
      setOrderId(order._id);

      if (selectedMethod === "khalti") {
        try {
          const paymentResponse = await api.post("/payments/khalti/initiate", {
            orderId: order._id,
          });
          window.location.href = paymentResponse.data.payment_url;
          return;
        } catch (paymentError) {
          setError(
            paymentError.response?.data?.message || t("checkout.paymentFailed"),
          );
          setLoading(false);
          return;
        }
      }

      if (selectedMethod === "esewa") {
        try {
          const paymentResponse = await api.post("/payments/esewa/initiate", {
            orderId: order._id,
          });
          const { esewaFormData } = paymentResponse.data;
          const form = document.createElement("form");
          form.method = "POST";
          form.action = esewaFormData.url;
          const { url, ...fields } = esewaFormData;
          Object.entries(fields).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
          return;
        } catch (paymentError) {
          setError(
            paymentError.response?.data?.message || t("checkout.paymentFailed"),
          );
          setLoading(false);
          return;
        }
      }

      setOrderPlaced(true);
      clearCart();
      setTimeout(() => navigate(`/order/${order._id}`), 3000);
    } catch (err) {
      setError(err.response?.data?.message || t("checkout.orderFailed"));
    } finally {
      setLoading(false);
    }
  };

  const subtotal = cartItems.reduce((sum, item) => {
    const price = item.product?.price || item.price || 0;
    return sum + price * (item.quantity || 1);
  }, 0);
  const deliveryFee =
    formData.deliveryMethod === "home" ? (subtotal > 500 ? 0 : 50) : 0;
  const total = subtotal + deliveryFee;

  const getProductImg = (product) => {
    const img = product?.image || (product?.images && product.images[0]);
    if (!img) return null;
    return getImageUrl(img, { width: 100, height: 100, crop: "fill" });
  };

  const itemCount = cartItems.reduce(
    (sum, item) => sum + (item.quantity || 1),
    0,
  );

  // Error state — empty cart
  if (error && cartItems.length === 0) {
    return (
      <div className="checkout-error-page">
        <Header />
        <div className="checkout-error-box">
          <p>{error}</p>
          <Link to="/cart" className="checkout-btn-back">
            {t("checkout.goToCart")}
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (orderPlaced) {
    return (
      <div className="checkout-success-page">
        <Header />
        <div className="checkout-success-card">
          <div className="checkout-success-check" aria-hidden>
            <FaCheck />
          </div>
          <h1>{t("checkout.orderSuccess")}</h1>
          <p>{t("checkout.confirmationEmail")}</p>
          <div className="checkout-success-order-id">
            {t("checkout.orderId", { id: orderId })}
          </div>
          <div className="checkout-success-btns">
            <Link
              to={`/order/${orderId}`}
              className="checkout-success-btn-primary"
            >
              {t("checkout.trackOrder")}
            </Link>
            <Link to="/orders" className="checkout-success-btn-secondary">
              {t("checkout.viewAllOrders")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <Header />

      <nav className="checkout-breadcrumb">
        <div className="checkout-breadcrumb-inner">
          <Link to="/marketplace" className="checkout-breadcrumb-link">
            {t("checkout.shop", "Shop")}
          </Link>
          <span className="checkout-breadcrumb-sep">›</span>
          <Link to="/cart" className="checkout-breadcrumb-link">
            {t("checkout.cart", "Cart")}
          </Link>
          <span className="checkout-breadcrumb-sep">›</span>
          <span className="checkout-breadcrumb-current">
            {t("checkout.title")}
          </span>
        </div>
      </nav>

      <main className="checkout-main">
        <div className="checkout-page-header">
          <h1 className="checkout-title">{t("checkout.title")}</h1>
          <span className="checkout-secure-badge">
            <svg
              className="checkout-secure-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {t("checkout.secureCheckout", "Secure Checkout")}
          </span>
        </div>

        <form onSubmit={handleSubmit} className="checkout-layout">
          {/* ===== LEFT COLUMN: Form Steps ===== */}
          <div className="checkout-form-col">
            {/* Step 1: Delivery Info */}
            <div className="checkout-step">
              <div className="checkout-step-header">
                <span className="checkout-step-number">1</span>
                <h2 className="checkout-step-title">
                  {t("checkout.deliveryInfo")}
                </h2>
              </div>
              <div className="checkout-step-body">
                <div className="checkout-form" role="group">
                  <div className="checkout-field-grid">
                    <div className="checkout-field">
                      <label htmlFor="fullName">{t("checkout.fullName")}</label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="checkout-field">
                      <label htmlFor="phone">{t("checkout.phone")}</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="address">{t("checkout.address")}</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder={t("checkout.streetPlaceholder")}
                      required
                    />
                  </div>
                  <div className="checkout-field-grid">
                    <div className="checkout-field">
                      <label htmlFor="city">
                        {t("checkout.cityMunicipality")}
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="checkout-field">
                      <label htmlFor="district">{t("checkout.district")}</label>
                      <select
                        id="district"
                        name="district"
                        value={formData.district}
                        onChange={handleChange}
                        required
                      >
                        <option value="Morang">Morang</option>
                        <option value="Sunsari">Sunsari</option>
                        <option value="Jhapa">Jhapa</option>
                      </select>
                    </div>
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="province">{t("checkout.province")}</label>
                    <input
                      type="text"
                      id="province"
                      name="province"
                      value={formData.province}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Delivery Method */}
            <div className="checkout-step">
              <div className="checkout-step-header">
                <span className="checkout-step-number">2</span>
                <h2 className="checkout-step-title">
                  {t("checkout.deliveryMethod")}
                </h2>
              </div>
              <div className="checkout-step-body">
                <div className="checkout-delivery-options">
                  <label
                    className={`checkout-option-card ${formData.deliveryMethod === "home" ? "selected" : ""}`}
                    onClick={() => selectOption("deliveryMethod", "home")}
                  >
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="home"
                      checked={formData.deliveryMethod === "home"}
                      onChange={handleChange}
                    />
                    <span className="checkout-option-check" />
                    <span className="checkout-option-icon">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="1" y="3" width="15" height="13" />
                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                        <circle cx="5.5" cy="18.5" r="2.5" />
                        <circle cx="18.5" cy="18.5" r="2.5" />
                      </svg>
                    </span>
                    <div className="checkout-option-info">
                      <div className="checkout-option-name">
                        {t("checkout.homeDelivery")}
                      </div>
                      <div className="checkout-option-desc">
                        {t("checkout.deliveryFeeLabel", {
                          fee:
                            subtotal > 500
                              ? t("common.free")
                              : `Rs. ${toLocalizedPrice(50)}`,
                        })}
                      </div>
                    </div>
                    <span
                      className={`checkout-option-tag ${subtotal > 500 ? "free" : "fee"}`}
                    >
                      {subtotal > 500
                        ? t("common.free")
                        : `Rs. ${toLocalizedPrice(50)}`}
                    </span>
                  </label>
                  <label
                    className={`checkout-option-card ${formData.deliveryMethod === "pickup" ? "selected" : ""}`}
                    onClick={() => selectOption("deliveryMethod", "pickup")}
                  >
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="pickup"
                      checked={formData.deliveryMethod === "pickup"}
                      onChange={handleChange}
                    />
                    <span className="checkout-option-check" />
                    <span className="checkout-option-icon">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </span>
                    <div className="checkout-option-info">
                      <div className="checkout-option-name">
                        {t("checkout.pickupFromFarm")}
                      </div>
                      <div className="checkout-option-desc">
                        {t("checkout.pickupDesc")}
                      </div>
                    </div>
                    <span className="checkout-option-tag free">
                      {t("common.free")}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Step 3: Payment Method */}
            <div className="checkout-step">
              <div className="checkout-step-header">
                <span className="checkout-step-number">3</span>
                <h2 className="checkout-step-title">
                  {t("checkout.paymentMethod")}
                </h2>
              </div>
              <div className="checkout-step-body">
                <div className="checkout-payment-options">
                  <label
                    className={`checkout-pay-card pay-cod ${formData.paymentMethod === "cod" ? "selected" : ""}`}
                    onClick={() => selectOption("paymentMethod", "cod")}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cod"
                      checked={formData.paymentMethod === "cod"}
                      onChange={handleChange}
                    />
                    <div className="checkout-pay-icon">
                      <img
                        src="/codicon.png"
                        alt="COD"
                        className="checkout-pay-logo"
                      />
                    </div>
                    <span className="checkout-pay-label">
                      {t("checkout.cod")}
                    </span>
                  </label>
                  <label
                    className={`checkout-pay-card pay-khalti ${formData.paymentMethod === "khalti" ? "selected" : ""}`}
                    onClick={() => selectOption("paymentMethod", "khalti")}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="khalti"
                      checked={formData.paymentMethod === "khalti"}
                      onChange={handleChange}
                    />
                    <div className="checkout-pay-icon">
                      <img
                        src="/khaltiicon.png"
                        alt="Khalti"
                        className="checkout-pay-logo"
                      />
                    </div>
                    <span className="checkout-pay-label">
                      {t("checkout.khalti")}
                    </span>
                  </label>
                  <label
                    className={`checkout-pay-card pay-esewa ${formData.paymentMethod === "esewa" ? "selected" : ""}`}
                    onClick={() => selectOption("paymentMethod", "esewa")}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="esewa"
                      checked={formData.paymentMethod === "esewa"}
                      onChange={handleChange}
                    />
                    <div className="checkout-pay-icon">
                      <img
                        src="/esewaicon.png"
                        alt="eSewa"
                        className="checkout-pay-logo"
                      />
                    </div>
                    <span className="checkout-pay-label">
                      {t("checkout.esewa")}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Step 4: Notes */}
            <div className="checkout-step">
              <div className="checkout-step-header">
                <span className="checkout-step-number">4</span>
                <h2 className="checkout-step-title">
                  {t("checkout.specialInstructions")}
                </h2>
              </div>
              <div className="checkout-step-body">
                <div className="checkout-field">
                  <textarea
                    id="specialInstructions"
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleChange}
                    rows="3"
                    placeholder={t("checkout.specialPlaceholder")}
                  />
                </div>
                <p className="checkout-instructions-note">
                  {t(
                    "checkout.instructionsNote",
                    "Optional — allergies, gate code, preferred time, etc.",
                  )}
                </p>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="checkout-error-banner">
                <svg
                  className="checkout-error-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="checkout-actions">
              <Link to="/cart" className="checkout-btn-back">
                ← {t("checkout.backToCart")}
              </Link>
              <button
                type="submit"
                className="checkout-btn-place-order"
                disabled={loading || cartItems.length === 0}
              >
                {loading ? (
                  <>
                    <span className="checkout-btn-spinner" />
                    {t("checkout.placingOrder")}
                  </>
                ) : (
                  <>
                    {t("checkout.placeOrder", {
                      total: toLocalizedPrice(total),
                    })}
                  </>
                )}
              </button>
            </div>

            <div className="checkout-trust-row">
              <span className="checkout-trust-item">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                {t("checkout.trustEncrypted", "Encrypted")}
              </span>
              <span className="checkout-trust-item">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                {t("checkout.trustSecure", "Secure Payment")}
              </span>
              <span className="checkout-trust-item">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
                {t("checkout.trustRefund", "Easy Refunds")}
              </span>
            </div>
          </div>

          {/* ===== RIGHT COLUMN: Order Summary ===== */}
          <div className="checkout-summary-col">
            <div className="checkout-summary-card">
              <div className="checkout-summary-header">
                <h3 className="checkout-summary-title">
                  {t("checkout.orderSummary")}
                </h3>
                <span className="checkout-summary-count">
                  {toLocalizedNumber(itemCount)}{" "}
                  {itemCount === 1
                    ? t("checkout.item", "item")
                    : t("checkout.items", "items")}
                </span>
              </div>

              <div className="checkout-summary-items">
                {cartItems.map((item, index) => {
                  const product = item.product || item;
                  const quantity = item.quantity || 1;
                  const price = product.price || 0;
                  const imgUrl = getProductImg(product);

                  return (
                    <div
                      key={item._id || product._id || index}
                      className="checkout-summary-item"
                    >
                      <div className="checkout-item-thumb">
                        {imgUrl ? (
                          <img src={imgUrl} alt={product.name} />
                        ) : (
                          <svg
                            className="checkout-item-thumb-placeholder"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#ccc"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              x="3"
                              y="3"
                              width="18"
                              height="18"
                              rx="2"
                              ry="2"
                            />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        )}
                      </div>
                      <div className="checkout-item-meta">
                        <Link
                          to={`/products/${product._id}`}
                          className="checkout-item-name"
                        >
                          {product.name}
                        </Link>
                        <div className="checkout-item-qty">
                          × {toLocalizedNumber(quantity)} {product.unit}
                        </div>
                      </div>
                      <span className="checkout-item-price">
                        Rs. {toLocalizedPrice(price * quantity)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="checkout-summary-totals">
                <div className="checkout-totals-row">
                  <span>{t("checkout.subtotal")}</span>
                  <span>Rs. {toLocalizedPrice(subtotal)}</span>
                </div>
                <div className="checkout-totals-row delivery">
                  <span>{t("checkout.deliveryFee")}</span>
                  <span
                    className={`checkout-totals-value ${deliveryFee === 0 ? "free" : ""}`}
                  >
                    {deliveryFee === 0
                      ? t("common.free")
                      : `Rs. ${toLocalizedPrice(deliveryFee)}`}
                  </span>
                </div>
                <div className="checkout-totals-divider" />
                <div className="checkout-totals-final">
                  <span className="checkout-totals-final-label">
                    {t("checkout.total")}
                  </span>
                  <span className="checkout-totals-final-value">
                    <span className="checkout-totals-final-currency">Rs.</span>
                    {toLocalizedPrice(total)}
                  </span>
                </div>
              </div>

              {subtotal <= 500 && formData.deliveryMethod === "home" && (
                <div className="checkout-promo-hint">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  {t(
                    "checkout.freeDeliveryHint",
                    { amount: toLocalizedPrice(500 - subtotal) },
                    `Add Rs. ${toLocalizedPrice(500 - subtotal)} more for free delivery`,
                  )}
                </div>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default Checkout;
